import { Search, FileText, Shield, AlertTriangle, CheckCircle, Scale } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const TitleSearch = () => {
  const mainContent = (
    <>
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Professional Title Search Services in Houston, Texas</h2>
            <p className="text-lg text-muted-foreground mb-8">
              A title search is a critical examination of public records to verify a property's legal ownership and uncover any claims, liens, or encumbrances that could affect your purchase. Our comprehensive title search services protect buyers, sellers, and lenders from costly disputes and ensure clear property ownership. With decades of experience in Texas real estate law, we conduct thorough title examinations that identify potential problems before they become major issues.
            </p>

            <div className="bg-accent/10 border-l-4 border-accent p-6 rounded-r-lg mb-12">
              <p className="text-lg font-semibold mb-2">
                <AlertTriangle className="inline h-5 w-5 mr-2 text-accent" />
                Why Title Searches Are Essential
              </p>
              <p className="text-muted-foreground">
                Hidden title defects can surface years after your purchase, threatening your ownership and property value. A professional title search reveals issues like unpaid taxes, undisclosed heirs, forged documents, easements, judgments, and liens before you close on the property. This protection is invaluable and can save you from financial disaster.
              </p>
            </div>

            <h3 className="text-3xl font-bold mb-8">What Our Title Search Includes</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Search className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Chain of Title Verification</h3>
                <p className="text-muted-foreground mb-4">
                  We trace property ownership back 30-50 years to ensure a complete and unbroken chain of title.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Review of all historical deeds and transfers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Verification of proper conveyances</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Identification of gaps in ownership</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Analysis of warranty vs quitclaim deeds</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Scale className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Lien & Judgment Search</h3>
                <p className="text-muted-foreground mb-4">
                  Comprehensive search for all liens, judgments, and encumbrances affecting the property.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Mortgage and deed of trust liens</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Federal and state tax liens (IRS, franchise tax)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Mechanic's and materialmen's liens</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>HOA liens and special assessments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Court judgments and child support liens</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <FileText className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Easements & Restrictions</h3>
                <p className="text-muted-foreground mb-4">
                  Identification of all easements, covenants, and restrictions that may limit property use.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Utility easements (electric, gas, water, sewer)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Access and ingress/egress easements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Building restrictions and setbacks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Deed restrictions and protective covenants</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Right-of-way agreements</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Shield className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Legal Description Verification</h3>
                <p className="text-muted-foreground mb-4">
                  Confirmation that legal descriptions match survey and ensure correct property boundaries.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Comparison of deed vs survey legal description</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Metes and bounds verification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Lot and block confirmation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Encroachment identification</span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-6">Common Title Issues We Discover</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {[
                { title: "Unpaid Property Taxes", desc: "Delinquent taxes that become liens on the property and must be paid at closing" },
                { title: "Undisclosed Heirs", desc: "Missing heirs from estates that could challenge ownership years later" },
                { title: "Forged Documents", desc: "Fraudulent signatures or transfers that invalidate the chain of title" },
                { title: "Errors in Public Records", desc: "Clerical mistakes, misspellings, or incorrect legal descriptions" },
                { title: "Unknown Liens", desc: "Hidden judgments, mechanic's liens, or IRS liens that attach to the property" },
                { title: "Boundary Disputes", desc: "Conflicting surveys or encroachments that affect property lines" },
                { title: "Missing Mortgages", desc: "Unreleased mortgages from previous owners that cloud the title" },
                { title: "Illegal Deeds", desc: "Transfers by individuals without legal capacity or authority" }
              ].map((issue, index) => (
                <div key={index} className="bg-card p-6 rounded-lg border border-accent/20">
                  <h4 className="font-bold text-lg mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-accent" />
                    {issue.title}
                  </h4>
                  <p className="text-sm text-muted-foreground">{issue.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Our Title Search Process</h2>
            <div className="space-y-6">
              {[
                { step: "1", title: "Property Identification", desc: "Confirm property address and legal description to ensure we search the correct parcel." },
                { step: "2", title: "Records Search", desc: "Search county records, tax assessor databases, court records, and federal lien indices." },
                { step: "3", title: "Document Review", desc: "Examine all deeds, mortgages, liens, judgments, easements, and restrictions affecting the property." },
                { step: "4", title: "Title Report Preparation", desc: "Compile findings into comprehensive title report detailing ownership history and all encumbrances." },
                { step: "5", title: "Issue Resolution", desc: "Work with parties to clear defects, release liens, and correct errors before closing." },
                { step: "6", title: "Title Insurance", desc: "Coordinate with title company to issue title insurance policy protecting your ownership." }
              ].map((item) => (
                <div key={item.step} className="flex gap-4 items-start bg-card p-6 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-gold-shine text-primary font-bold flex items-center justify-center">
                    {item.step}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold mb-1">{item.title}</h4>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Why Choose Our Title Search Services?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Search className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Thorough Examination</h3>
                <p className="text-muted-foreground">We don't cut corners. Our comprehensive searches go beyond basic requirements</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Shield className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">20+ Years Experience</h3>
                <p className="text-muted-foreground">Decades of experience identifying and resolving complex title issues</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <FileText className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Clear Reporting</h3>
                <p className="text-muted-foreground">Easy-to-understand reports that explain findings and recommended actions</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );

  return (
    <GenericPracticePage
      title="Title Search Services"
      subtitle="Comprehensive Property Title Examination in Houston, Texas"
      icon={<Search className="h-16 w-16" />}
      mainContent={mainContent}
    />
  );
};

export default TitleSearch;